/**
 * @file `<Term/>` — inline glossary tooltip, plus the glossary context
 *       that feeds it.
 * @module @labcraft/course-kit/components/term
 *
 * The context-driven counterpart `glossary.tsx`'s JSDoc anticipated:
 * the platform wraps the chapter's `<MdxProvider/>` in a
 * `<GlossaryProvider terms={course.glossary}>`, and chapter prose
 * marks inline mentions as `<Term slug="closure">closures</Term>` —
 * hover/focus shows the glossary entry's `short` + `body` without
 * leaving the page.
 *
 * The tooltip is rendered inline (always in the DOM, referenced by
 * `aria-describedby`) so static rendering + a11y see it, but painted
 * `position: fixed` and positioned in JS on open. Fixed positioning is
 * deliberate: the reader's content pane scrolls under `overflow: auto`
 * / `overflow: hidden`, and an absolutely-positioned tip is *clipped* by
 * that scroll box — a right-edge term's tip would be cut off at the
 * terminal boundary. Fixed escapes the clip (no transformed ancestor in
 * the reader chain), the JS clamps it to the viewport so it grows inward
 * instead of overflowing, and `z-index` sits it above the terminal +
 * chapter chrome but below the reader's modals. The tip only becomes
 * visible once positioned, so there's no wrong-spot flash.
 *
 * An unknown slug warns to the console and renders the children as
 * plain text (mirrors `<Objective>`'s warn-and-skip): a typo'd slug
 * degrades to prose, never a crash.
 */

import {
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react'
import type { Term as GlossaryTerm } from '../types.ts'
import { GlossaryContext } from './glossary_context.ts'
import { renderInline } from './glossary.tsx'
import styles from './term.module.css'

/**
 * Props for `<GlossaryProvider/>`.
 *
 * @property terms The active course's glossary (each course package
 *           exports a plain `GLOSSARY`; the lab feeds it from the
 *           chapter response's course header).
 * @property children Subtree that may render `<Term/>`.
 */
export interface GlossaryProviderProps {
  terms: readonly GlossaryTerm[]
  children: ReactNode
}

/**
 * Provides the course glossary to inline `<Term/>` tooltips. Wrap it
 * around `<MdxProvider/>` on chapter surfaces.
 *
 * @param props See {@link GlossaryProviderProps}.
 * @returns The provider element.
 */
export function GlossaryProvider(props: GlossaryProviderProps): JSX.Element {
  return (
    <GlossaryContext.Provider value={props.terms}>
      {props.children}
    </GlossaryContext.Provider>
  )
}

/**
 * Props for `<Term/>`.
 *
 * @property slug Glossary entry slug to look up (`Term.slug` in the
 *           course's `GLOSSARY`).
 * @property children The inline prose being marked (usually the term
 *           as it appears in the sentence, any inflection).
 */
export interface TermProps {
  slug: string
  children: ReactNode
}

/** Resolved viewport coordinates for the fixed tooltip. */
type TipPos = { top: number; left: number }

/**
 * Inline glossary mention: dotted-underlined span whose hover/focus
 * tooltip shows the glossary entry's label, `short` gloss, and `body`.
 * The tip is `position: fixed`, positioned + viewport-clamped in JS on
 * open, so it never gets clipped by the scrolling content pane (see the
 * `@file` note).
 *
 * @param props See {@link TermProps}.
 * @returns The marked span, or the bare children when the slug isn't
 *          in the active glossary.
 */
export function Term(props: TermProps): JSX.Element {
  const terms = useContext(GlossaryContext)
  const tipId = useId()
  const anchorRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<TipPos | null>(null)

  // On open: measure the anchor + tip, place the tip above the term
  // (flip below when there's no headroom), and clamp horizontally so a
  // right-edge term's tip grows inward instead of overflowing behind the
  // terminal pane. A fixed tip detaches from the scrolling term, so close
  // on scroll/resize rather than chase it. Runs as a passive effect (not
  // layout effect) to stay SSR-quiet; the tip only shows once `pos` is
  // set, so the post-paint measure never flashes at the wrong spot.
  useEffect(() => {
    if (!open) return
    const anchor = anchorRef.current
    const tip = tipRef.current
    if (!anchor || !tip) return

    const a = anchor.getBoundingClientRect()
    const t = tip.getBoundingClientRect()
    const margin = 8
    const gap = 8
    const left = Math.max(
      margin,
      Math.min(
        a.left + a.width / 2 - t.width / 2,
        window.innerWidth - t.width - margin,
      ),
    )
    const above = a.top - gap - t.height
    setPos({ top: above >= margin ? above : a.bottom + gap, left })

    const close = (): void => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const term = terms.find(t => t.slug === props.slug)
  if (!term) {
    if (typeof console !== 'undefined') {
      console.warn(
        `<Term slug="${props.slug}"> not found in the course glossary; rendering plain text.`,
      )
    }
    return <>{props.children}</>
  }

  const show = (): void => setOpen(true)
  const hide = (): void => {
    setOpen(false)
    setPos(null)
  }

  const visible = open && pos !== null
  const tipStyle: CSSProperties | undefined = visible
    ? { top: pos.top, left: pos.left }
    : undefined

  return (
    <span
      ref={anchorRef}
      className={styles.term}
      tabIndex={0}
      aria-describedby={tipId}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}>
      {props.children}
      <span
        ref={tipRef}
        role="tooltip"
        id={tipId}
        className={visible ? `${styles.tip} ${styles.tipOpen}` : styles.tip}
        style={tipStyle}>
        <span className={styles.tipLabel}>{term.label}</span>
        <span className={styles.tipShort}>{renderInline(term.short)}</span>
        <span className={styles.tipBody}>{renderInline(term.body)}</span>
      </span>
    </span>
  )
}
